'use client';

import * as React from 'react';
import { ChangeEvent, MouseEvent } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckIcon, MinusIcon } from 'lucide-react';

import { cn } from './utils';
import { Tooltip } from './tooltip';

// --- shadcn Checkbox (Radix primitive) ---

function CheckboxPrimary({ className, ...props }: React.ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        'bg-input-background dark:bg-input/30 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive shadow-xs peer size-5 shrink-0 rounded-[4px] border border-neutral-400 outline-none transition-shadow data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground hover:border-therablue focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-therablue disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current transition-none"
      >
        {props.checked === 'indeterminate' ? <MinusIcon className="size-4" /> : <CheckIcon className="size-4" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

// --- Checkbox with legacy API (label, tooltipContent, onChange) backed by shadcn ---

interface CheckboxProps {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  value?: string;
  checked: boolean | 'indeterminate';
  label?: string;
  disabled?: boolean;
  className?: string;
  tooltipContent?: string;
}

const Checkbox = ({ onChange, onClick, value, label, checked, disabled, tooltipContent, className }: CheckboxProps) => {
  // Bridge: Radix onCheckedChange → legacy onChange ChangeEvent shape
  const handleCheckedChange = (nextChecked: boolean | 'indeterminate') => {
    const syntheticEvent = {
      target: { checked: nextChecked === true, value: value || '' },
    } as ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
  };

  const checkbox = (
    <div className="flex cursor-pointer items-center">
      <CheckboxPrimary
        checked={checked}
        onCheckedChange={handleCheckedChange}
        onClick={onClick as unknown as React.MouseEventHandler}
        disabled={disabled}
        className={className}
        value={value}
      />
      {Boolean(label?.length) && (
        <span className="ml-2 inline text-sm" onClick={() => !disabled && handleCheckedChange(checked !== true)}>
          {label}
        </span>
      )}
    </div>
  );

  if (tooltipContent) {
    return (
      <Tooltip
        content={tooltipContent}
        className={`text-xs ${disabled && tooltipContent ? 'cursor-not-allowed' : 'hidden'}`}
      >
        {checkbox}
      </Tooltip>
    );
  }

  return checkbox;
};

export { Checkbox, CheckboxPrimary };
